import { StructuredData } from '~/components/structured-data'
import {
  ChevronRight, Calendar, User, Clock, CheckCircle,
  AlertCircle, Star,
} from 'lucide-react'

// ─── Table of Contents ───────────────────────────────────────

interface TocItem {
  id: string
  label: string
}

export function TableOfContents({ items }: { items: TocItem[] }) {
  return (
    <nav className="rounded-xl border border-border bg-white p-5 shadow-xs" aria-label="Sommaire">
      <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <ChevronRight className="h-4 w-4 text-primary" />
        Sommaire de cette page
      </h2>
      <ol className="mt-3 space-y-1.5">
        {items.map((item, i) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-slate-600 transition-colors hover:bg-primary-50 hover:text-primary"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-500">{i + 1}</span>
              {item.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  )
}

// ─── Article Meta (author, date, reading time) ───────────────

export function ArticleMeta({ readingTime = 8 }: { readingTime?: number }) {
  const today = new Date()
  const updateDate = today.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
      <span className="flex items-center gap-1">
        <User className="h-3 w-3" />
        Par l'équipe AfficheServices
      </span>
      <span className="flex items-center gap-1">
        <Calendar className="h-3 w-3" />
        Mis à jour le {updateDate}
      </span>
      <span className="flex items-center gap-1">
        <Clock className="h-3 w-3" />
        {readingTime} min de lecture
      </span>
    </div>
  )
}

// ─── Live Stats Bar (real DB data) ───────────────────────────

export function PageStats({ proCount, avgRating, reviewCount, cityName }: {
  proCount: number
  avgRating: string | null
  reviewCount: number
  cityName: string
}) {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-xl bg-slate-50 px-4 py-3 text-xs">
      {proCount > 0 && (
        <span className="flex items-center gap-1.5 font-medium text-slate-700">
          <span className="flex h-2 w-2 rounded-full bg-emerald-500" />
          <strong className="text-slate-900">{proCount}</strong> artisan{proCount > 1 ? 's' : ''} vérifié{proCount > 1 ? 's' : ''} à {cityName}
        </span>
      )}
      {avgRating && parseFloat(avgRating) > 0 && (
        <span className="flex items-center gap-1 text-slate-600">
          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
          <strong>{avgRating}</strong>/5 ({reviewCount} avis)
        </span>
      )}
      {proCount === 0 && (
        <span className="flex items-center gap-1.5 text-slate-600">
          <CheckCircle className="h-3 w-3 text-emerald-500" />
          Chaque artisan vérifié (SIRET, RC Pro, décennale) avant mise en relation
        </span>
      )}
    </div>
  )
}

// ─── Article Schema (for Google) ─────────────────────────────

/**
 * Article schema — full-grade signal pack для Google rich snippets + AI
 * citations. Includes speakable (Assistant TTS surfaces), keywords + section
 * (topical context), wordCount + inLanguage (quality + locale), ImageObject
 * with dimensions (visual rich result), isAccessibleForFree (paywall hint).
 */
export function ArticleSchema({
  title,
  description,
  url,
  datePublished,
  dateModified,
  image,
  keywords,
  articleSection,
  wordCount,
  authorName,
  authorUrl,
}: {
  title: string
  description: string
  url: string
  datePublished?: string
  dateModified?: string
  /** Article hero image — passed as URL or ImageObject. */
  image?: string | null
  /** Tags or topic keywords. Comma-joined for Schema. */
  keywords?: string[] | null
  /** Top-level category (Conseils, Guides, Prix, etc.). */
  articleSection?: string | null
  /** Total content word count. Quality + AI consumption signal. */
  wordCount?: number | null
  authorName?: string
  authorUrl?: string
}) {
  const today = new Date().toISOString().split('T')[0]
  return (
    <StructuredData data={{
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description,
      url,
      inLanguage: 'fr-FR',
      isAccessibleForFree: true,
      datePublished: datePublished ?? today,
      dateModified: dateModified ?? today,
      ...(image ? {
        image: {
          '@type': 'ImageObject',
          url: image,
          width: '1200',
          height: '630',
        },
      } : {}),
      ...(keywords && keywords.length > 0 ? { keywords: keywords.join(', ') } : {}),
      ...(articleSection ? { articleSection } : {}),
      ...(wordCount && wordCount > 0 ? { wordCount } : {}),
      author: {
        '@type': authorName ? 'Person' : 'Organization',
        name: authorName ?? 'Équipe éditoriale AfficheServices',
        url: authorUrl ?? 'https://www.afficheservices.fr',
      },
      publisher: {
        '@type': 'Organization',
        '@id': 'https://www.afficheservices.fr/#org',
        name: 'AfficheServices',
        url: 'https://www.afficheservices.fr',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.afficheservices.fr/og-image.png',
          width: '512',
          height: '512',
        },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      // Speakable — Google Assistant TTS surface. Targets the article H1 +
      // first paragraph for voice-driven summarization.
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', 'article > .key-takeaways', 'article > p:first-of-type'],
      },
    }} />
  )
}

// ─── Process Steps Schema ────────────────────────────────────
// Was HowTo until Sept 2023 — Google retired HowTo rich results.
// Now emit ItemList of ListItem to remain valid + AI-readable.
// Component name kept for backwards-compat with existing route imports.

export function HowToSchema({ name, steps }: {
  name: string
  steps: Array<{ title: string; detail: string }>
}) {
  return (
    <StructuredData data={{
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name,
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      numberOfItems: steps.length,
      itemListElement: steps.map((s, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: s.title,
        description: s.detail,
      })),
    }} />
  )
}

// ─── Comparison Table (with vs without) ──────────────────────

export function ComparisonTable({ serviceName: _serviceName }: { serviceName: string }) {
  return (
    <div className="rounded-2xl border border-border bg-white shadow-xs overflow-hidden">
      <div className="border-b border-border px-4 py-4 sm:px-6">
        <h2 className="text-lg font-bold text-slate-900">Avec vs Sans AfficheServices</h2>
      </div>
      {(() => {
        const rows = [
          { criteria: 'Recherche d\'artisan', without: 'Pages jaunes, bouche-à-oreille', with: 'Artisans vérifiés, triés par note' },
          { criteria: 'Vérification', without: 'Aucune garantie', with: 'SIRET + RC Pro + identité contrôlés' },
          { criteria: 'Nombre de devis', without: '1 seul devis', with: 'Jusqu\'à 3 devis comparatifs' },
          { criteria: 'Délai', without: 'Plusieurs jours de recherche', with: 'Devis sous 48h' },
          { criteria: 'Prix', without: 'Aucune référence', with: 'Grille tarifaire transparente' },
          { criteria: 'En cas de problème', without: 'Vous êtes seul', with: 'Médiation AfficheServices' },
          { criteria: 'Coût du service', without: '—', with: '100% gratuit' },
        ]
        return (
          <>
            {/* Mobile: stacked cards (avoids cramped 3-col table on small screens) */}
            <div className="divide-y divide-border sm:hidden">
              {rows.map((row, i) => (
                <div key={i} className="px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{row.criteria}</p>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div className="rounded-lg bg-red-50/50 p-2.5">
                      <p className="text-[10px] font-semibold uppercase text-red-500">Sans plateforme</p>
                      <p className="mt-1 flex items-start gap-1 text-slate-600">
                        <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-red-400" />
                        <span>{row.without}</span>
                      </p>
                    </div>
                    <div className="rounded-lg bg-emerald-50/50 p-2.5">
                      <p className="text-[10px] font-semibold uppercase text-emerald-600">Avec AfficheServices</p>
                      <p className="mt-1 flex items-start gap-1 font-medium text-slate-900">
                        <CheckCircle className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
                        <span>{row.with}</span>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: classic comparison table */}
            <div className="hidden sm:block">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-6 py-2.5">Critère</th>
                    <th className="px-6 py-2.5 text-center">Sans plateforme</th>
                    <th className="px-6 py-2.5 text-center">Avec AfficheServices</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-sm">
                  {rows.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-6 py-3 font-medium text-slate-900">{row.criteria}</td>
                      <td className="px-6 py-3 text-center text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <AlertCircle className="h-3 w-3 text-red-400" />
                          {row.without}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-center text-slate-900">
                        <span className="inline-flex items-center gap-1 font-medium">
                          <CheckCircle className="h-3 w-3 text-emerald-500" />
                          {row.with}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      })()}
    </div>
  )
}
